import { createFileRoute, redirect } from '@tanstack/react-router';
import { z } from 'zod';
import { IntegrationDataType } from '@mews-commander/data-access';
import {
    EnterpriseIntegrationUpdateContainer,
    integrationDataTypeFallbackUrlHandler,
    integrationDataTypeTextKeyHandler,
    updateComponentHandler,
} from '@mews-commander/enterprise-integration';
import { MewsTeam, RouteType } from '@mews/enums';

const isIntegrationDataType = (value: string): value is IntegrationDataType =>
    Object.values<string>(IntegrationDataType).includes(value);

const Component = () => {
    const { id } = Route.useParams();
    const { integrationName, targetEnterpriseId } = Route.useSearch();

    if (!isIntegrationDataType(integrationName)) {
        throw new Error(`${integrationName} is an invalid IntegrationDataType`);
    }

    return (
        <EnterpriseIntegrationUpdateContainer
            id={id}
            integrationName={integrationName}
            targetEnterpriseId={targetEnterpriseId}
        />
    );
};

export const Route = createFileRoute('/_authenticated/$currentEnterpriseId/EnterpriseIntegration/$id/Update')({
    validateSearch: z.object({
        integrationName: z.string(),
        targetEnterpriseId: z.string().uuid(),
    }),
    beforeLoad: ({
        params: { currentEnterpriseId, id },
        search: { integrationName },
        context: { featureFlagService },
    }) => {
        if (!isIntegrationDataType(integrationName)) {
            throw new Error(`${integrationName} is an invalid IntegrationDataType`);
        }

        const frontendComponentExists = updateComponentHandler(integrationName, {
            featureFlagService,
        });

        if (!frontendComponentExists) {
            // Redirect to legacy BE routes
            const to = integrationDataTypeFallbackUrlHandler(integrationName);

            if (!to) {
                throw new Error(`${integrationName} has no valid fallback URL`);
            }

            throw redirect({
                to,
                params: {
                    currentEnterpriseId,
                    id,
                },
            });
        }
    },
    loaderDeps: ({ search: { integrationName } }) => ({ integrationName }),
    loader: ({ deps: { integrationName }, context: { localizeText } }) => {
        if (!isIntegrationDataType(integrationName)) {
            throw new Error(`${integrationName} is an invalid IntegrationDataType`);
        }
        const textKey = integrationDataTypeTextKeyHandler(integrationName);

        return {
            title: () => localizeText({ textKey }),
        };
    },
    component: Component,
    staticData: {
        owner: MewsTeam.ActivationExperience,
        type: RouteType.Frontend,
    },
});
